/**
 * API utilities for consistent HTTP requests and error handling
 * These functions provide standardized API communication patterns
 */

// ================================
// HTTP client configuration
// ================================

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers?: Record<string, string>;
  body?: any;
  timeout?: number;
  retries?: number;
  retryDelay?: number;
}

interface ApiResponse<T = any> {
  data: T | null;
  success: boolean;
  error: string | null;
  status: number;
  headers: Headers;
}

interface ApiError {
  message: string;
  status: number;
  code?: string;
  details?: any;
}

// ================================
// BASE API client
// ================================

/**
 * Enhanced fetch wrapper with error handling and retries
 * @param url - Request URL
 * @param options - Request options
 * @returns Promise resolving to ApiResponse
 */
export async function apiRequest<T = any>(
  url: string,
  options: RequestOptions = {}
): Promise<ApiResponse<T>> {
  const {
    method = 'GET',
    headers = {},
    body,
    timeout = 10000,
    retries = 1,
    retryDelay = 1000
  } = options;

  // Prepare request configuration
  const config: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...headers
    }
  };

  // Add body if provided
  if (body && method !== 'GET') {
    config.body = typeof body === 'string' ? body : JSON.stringify(body);
  }

  // Create abort controller for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  let lastError: Error | null = null;

  // Retry logic
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetch(url, {
        ...config,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      // Parse response
      let data: T | null = null;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        try {
          data = await response.json();
        } catch (parseError) {
          }
      }

      return {
        data,
        success: response.ok,
        error: response.ok ? null : getErrorMessage(response, data),
        status: response.status,
        headers: response.headers
      };

    } catch (error) {
      lastError = error as Error;

      // Don't retry on abort (timeout) or network errors on last attempt
      if (attempt === retries || error instanceof DOMException) {
        break;
      }

      // Wait before retry
      await new Promise(resolve => setTimeout(resolve, retryDelay * (attempt + 1)));
    }
  }

  clearTimeout(timeoutId);

  // Return error response
  return {
    data: null,
    success: false,
    error: lastError?.message || 'Request failed',
    status: 0,
    headers: new Headers()
  };
}

/**
 * Extracts error message from response
 * @param response - Fetch Response object
 * @param data - Parsed response data
 * @returns Error message string
 */
function getErrorMessage(response: Response, data: any): string {
  // Try to get error from response data
  if (data && typeof data === 'object') {
    if (data.error) return data.error;
    if (data.message) return data.message;
    if (data.details) return data.details;
  }

  // Fallback to status text
  return response.statusText || `HTTP ${response.status}`;
}

// ================================
// HTTP METHOD HELPERS
// ================================

/**
 * GET request helper
 * @param url - Request URL
 * @param headers - Additional headers
 * @returns Promise resolving to ApiResponse
 */
export function apiGet<T = any>(
  url: string,
  headers?: Record<string, string>
): Promise<ApiResponse<T>> {
  return apiRequest<T>(url, { method: 'GET', headers });
}

/**
 * POST request helper
 * @param url - Request URL
 * @param body - Request body
 * @param headers - Additional headers
 * @returns Promise resolving to ApiResponse
 */
export function apiPost<T = any>(
  url: string,
  body?: any,
  headers?: Record<string, string>
): Promise<ApiResponse<T>> {
  return apiRequest<T>(url, { method: 'POST', body, headers });
}

/**
 * PUT request helper
 * @param url - Request URL
 * @param body - Request body
 * @param headers - Additional headers
 * @returns Promise resolving to ApiResponse
 */
export function apiPut<T = any>(
  url: string,
  body?: any,
  headers?: Record<string, string>
): Promise<ApiResponse<T>> {
  return apiRequest<T>(url, { method: 'PUT', body, headers });
}

/**
 * PATCH request helper
 * @param url - Request URL
 * @param body - Request body
 * @param headers - Additional headers
 * @returns Promise resolving to ApiResponse
 */
export function apiPatch<T = any>(
  url: string,
  body?: any,
  headers?: Record<string, string>
): Promise<ApiResponse<T>> {
  return apiRequest<T>(url, { method: 'PATCH', body, headers });
}

/**
 * DELETE request helper
 * @param url - Request URL
 * @param headers - Additional headers
 * @returns Promise resolving to ApiResponse
 */
export function apiDelete<T = any>(
  url: string,
  headers?: Record<string, string>
): Promise<ApiResponse<T>> {
  return apiRequest<T>(url, { method: 'DELETE', headers });
}

// ================================
// FILE UPLOAD UTILITIES
// ================================

/**
 * Uploads file with progress tracking
 * @param url - Upload URL
 * @param file - File to upload
 * @param onProgress - Progress callback
 * @param additionalData - Additional form data
 * @returns Promise resolving to ApiResponse
 */
export function uploadFile<T = any>(
  url: string,
  file: File,
  onProgress?: (progress: number) => void,
  additionalData?: Record<string, string>
): Promise<ApiResponse<T>> {
  return new Promise((resolve) => {
    const formData = new FormData();
    formData.append('file', file);

    // Add additional data
    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    const xhr = new XMLHttpRequest();

    // Track upload progress
    if (onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = (event.loaded / event.total) * 100;
          onProgress(progress);
        }
      });
    }

    xhr.addEventListener('load', () => {
      let data: T | null = null;
      let error: string | null = null;

      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          data = xhr.responseText ? JSON.parse(xhr.responseText) : null;
        } catch (parseError) {
          }
      } else {
        error = `Upload failed with status ${xhr.status}`;
      }

      resolve({
        data,
        success: xhr.status >= 200 && xhr.status < 300,
        error,
        status: xhr.status,
        headers: new Headers()
      });
    });

    xhr.addEventListener('error', () => {
      resolve({
        data: null,
        success: false,
        error: 'Upload failed',
        status: 0,
        headers: new Headers()
      });
    });

    xhr.open('POST', url);
    xhr.send(formData);
  });
}

// ================================
// URL UTILITIES
// ================================

/**
 * Builds URL with query parameters
 * @param baseUrl - Base URL
 * @param params - Query parameters object
 * @returns Complete URL with parameters
 */
export function buildUrl(baseUrl: string, params?: Record<string, any>): string {
  if (!params || Object.keys(params).length === 0) {
    return baseUrl;
  }

  const url = new URL(baseUrl);

  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      if (Array.isArray(value)) {
        value.forEach(item => url.searchParams.append(key, String(item)));
      } else {
        url.searchParams.append(key, String(value));
      }
    }
  });

  return url.toString();
}

/**
 * Extracts query parameters from URL
 * @param url - URL string
 * @returns Object with query parameters
 */
export function getUrlParams(url: string): Record<string, string> {
  try {
    const urlObj = new URL(url);
    const params: Record<string, string> = {};

    urlObj.searchParams.forEach((value, key) => {
      params[key] = value;
    });

    return params;
  } catch {
    return {};
  }
}

// ================================
// RESPONSE UTILITIES
// ================================

/**
 * Checks if API response indicates success
 * @param response - API response object
 * @returns True if response is successful
 */
export function isSuccessResponse<T>(response: ApiResponse<T>): response is ApiResponse<T> & { data: T } {
  return response.success && response.data !== null;
}

/**
 * Throws error if response is not successful
 * @param response - API response object
 * @throws ApiError if response is not successful
 */
export function assertSuccessResponse<T>(response: ApiResponse<T>): asserts response is ApiResponse<T> & { data: T } {
  if (!isSuccessResponse(response)) {
    const error: ApiError = {
      message: response.error || 'API request failed',
      status: response.status,
      details: response.data
    };
    throw error;
  }
}

// ================================
// cache UTILITIES
// ================================

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

class ApiCache {
  private cache = new Map<string, CacheEntry<any>>();

  set<T>(key: string, data: T, ttl: number = 300000): void { // 5 minutes default
    const now = Date.now();
    this.cache.set(key, {
      data,
      timestamp: now,
      expiresAt: now + ttl
    });
  }

  get<T>(key: string): T | null {
    const entry = this.cache.get(key);

    if (!entry) return null;

    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  delete(key: string): void {
    this.cache.delete(key);
  }

  clear(): void {
    this.cache.clear();
  }

  cleanup(): void {
    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      if (now > entry.expiresAt) {
        this.cache.delete(key);
      }
    }
  }
}

export const apiCache = new ApiCache();

// Cleanup expired cache entries every 5 minutes
if (typeof window !== 'undefined') {
  setInterval(() => apiCache.cleanup(), 300000);
}
